import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import DesignSystem from '../designSystem';
import { useT } from '../i18n/useT';
import VisiWizardPage from './VisiWizardPage';

type CreateLocationState = {
  promoterCode?: string;
};

export default function VisiCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const promoterCode = (location.state as CreateLocationState | null)?.promoterCode?.trim() || '';
  const [name, setName] = useState('');
  const [started, setStarted] = useState(false);

  const trimmedName = useMemo(() => name.trim(), [name]);
  const canCreate = trimmedName.length > 0;

  if (started) {
    return <VisiWizardPage initialName={trimmedName} promoterCode={promoterCode || undefined} />;
  }

  return (
    <Dialog
      open
      onClose={() => navigate(-1)}
      fullWidth
      maxWidth="sm"
      scroll="body"
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
        },
      }}
      PaperProps={{
        sx: {
          m: 0,
          position: { xs: 'fixed', sm: 'relative' },
          bottom: { xs: 0, sm: 'auto' },
          left: { xs: 0, sm: 'auto' },
          right: { xs: 0, sm: 'auto' },
          width: { xs: '100%', sm: 'auto' },
          maxWidth: { xs: '100%', sm: 'sm' },
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          borderBottomLeftRadius: { xs: 0, sm: 20 },
          borderBottomRightRadius: { xs: 0, sm: 20 },
          bgcolor: DesignSystem.colors.surface,
          backgroundImage: 'none',
          px: { xs: 2.5, sm: 3 },
          pt: 2,
          pb: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 24px)', sm: 3 },
        },
      }}
    >
      <Box sx={{ position: 'relative', textAlign: 'center' }}>
        <IconButton
          aria-label={t('cancel')}
          onClick={() => navigate(-1)}
          sx={{
            position: 'absolute',
            top: 0,
            right: -8,
            color: DesignSystem.colors.textPrimary,
          }}
        >
          <Close />
        </IconButton>

        <Typography
          sx={{
            mt: 3.5,
            mb: 5,
            px: 1,
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.35,
            color: DesignSystem.colors.textPrimary,
          }}
        >
          {t('createVisiInstructionTitle')}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          variant="standard"
          placeholder={t('createVisiNamePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canCreate) {
              setStarted(true);
            }
          }}
          inputProps={{
            'aria-label': t('createVisiNamePlaceholder'),
            style: {
              textAlign: 'center',
              fontSize: 28,
              fontWeight: 700,
            },
          }}
          sx={{
            px: 1,
            '& .MuiInput-root:before': {
              borderBottomColor: DesignSystem.colors.primary,
              borderBottomWidth: 2,
            },
            '& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottomColor: DesignSystem.colors.primary,
            },
            '& .MuiInput-root:after': {
              borderBottomColor: DesignSystem.colors.primary,
            },
            '& .MuiInputBase-input': {
              color: DesignSystem.colors.primary,
              py: 1,
            },
            '& .MuiInputBase-input::placeholder': {
              color: DesignSystem.colors.textSecondary,
              opacity: 0.55,
            },
          }}
        />

        <Button
          variant="contained"
          disabled={!canCreate}
          onClick={() => setStarted(true)}
          sx={{
            mt: 5,
            minWidth: 150,
            height: 50,
            borderRadius: DesignSystem.borderRadius.pill,
            textTransform: 'none',
            fontWeight: 650,
            fontSize: 17,
            boxShadow: 'none',
            opacity: canCreate ? 1 : 0.5,
            backgroundColor: DesignSystem.colors.primary,
            '&:hover': {
              backgroundColor: DesignSystem.colors.actionDark,
              boxShadow: 'none',
            },
          }}
        >
          {t('createVisiButton')}
        </Button>
      </Box>
    </Dialog>
  );
}
